import { defineStore } from 'pinia'
import _debounce from 'lodash/debounce'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import type { UnitState, LoadedSlots } from '@/types/unit-types'

/**
 * Powers FB and Google marketing units.
 */
export const useUnitsStore = defineStore('units', {
  state: (): UnitState => ({
    unitSlotPercents:
      window.Laravel && window.Laravel.units && window.Laravel.units.unitSlotPercents
        ? window.Laravel.units.unitSlotPercents
        : null,

    preview:
      window.Laravel && window.Laravel.units && window.Laravel.units.preview
        ? window.Laravel.units.preview
        : 0,

    queuedSlots: [],

    // loadedSlots.key format = unitSlotId_index
    loadedSlots: {},
  }),

  getters: {
    getUnit: (state: UnitState) => (unitSlotId: string, queuedIndex: number) => {
      const loadedSlotKey = `${unitSlotId}_${queuedIndex}`

      if (!state.loadedSlots || !state.loadedSlots[loadedSlotKey]) {
        return null
      }

      return state.loadedSlots[loadedSlotKey]
    },

    isUnitPreview(state: UnitState): boolean {
      return !!state.preview
    },
  },

  actions: {
    queueUnitSlot(unitSlotId: number) {
      this.queuedSlots.push(unitSlotId)
    },

    resetQueuedUnitSlots() {
      this.queuedSlots = []
    },

    setLoadedUnitSlots(loadedSlots: LoadedSlots) {
      this.loadedSlots = loadedSlots
    },

    appendLoadedUnitSlots(loadedSlots: LoadedSlots) {
      this.loadedSlots = {
        ...this.loadedSlots,
        ...loadedSlots,
      }
    },

    removeLoadedUnitSlot(unitSlotId: string, queuedIndex: string) {
      const updatedLoadedSlots = {
        ...this.loadedSlots,
      }

      delete updatedLoadedSlots[`${unitSlotId}_${queuedIndex}`]

      this.loadedSlots = updatedLoadedSlots
    },

    /**
     * Fetch units for the queued unitSlotIds, populates loadedSlots
     * then clears the queue.
     *
     * Should not use directly, use initUnitSlot.
     */
    fetchUnitSlots: _debounce(async (unitsStore: any) => {
      // console.log('fetchUnitSlots invoked')

      // step 1 copy queue and clear
      // step 2 fetch (if
      // step 2 format populate loadedSlots via setLoadedUnitSlots mutation
      try {
        // copy queue ids, then clear queue quickly in case user
        // navigates off the page before XHR finishes
        const queue = [...unitsStore.queuedSlots]
        unitsStore.resetQueuedUnitSlots()

        // dynamic body to support fb admin preview
        const params = {} as any
        params.unitSlotIds = queue

        // if queue is empty, do not call XHR
        // may happen due to adblockers
        if (!Array.isArray(params.unitSlotIds) || params.unitSlotIds.length === 0) {
          throw new Error(`Unit slots missing: ${params.unitSlotIds.toString()}`)
        } else {
          // TODO: units remove after figuring out 4xx
          // seeing some 4xx errors and unsure why, adding additional logging
          console.log(`FB: unit slots to fill: ${params.unitSlotIds.toString()}`)
        }

        if (unitsStore.preview) {
          params.preview = unitsStore.preview
        }
        // end body

        const res = await fbApi.get('/units', {
          searchParams: objToSearchParams(params),
          // body,
          // do not show error modal on 4xx
          skipErrorModal: true,
        })

        if (!res || !res.body || !Array.isArray(res.body.data)) {
          throw new Error(`Missing unit data`)
        }

        // transform array to object with key `unitSlotId_arrayIndex`
        const loadedUnitSlots = res.body.data.reduce((acc: any, unit: any, index: any) => {
          acc[`${unit.unitSlotId}_${index}`] = unit

          return acc
        }, {})

        // unitsStore.setLoadedUnitSlots(loadedUnitSlots)
        unitsStore.appendLoadedUnitSlots(loadedUnitSlots)
      } catch (err) {
        // do not show error modal, just log
        console.error('units unable to load', err)
      }
    }, 10),

    /**
     * Adds a new unitSlotId to the queue then waits for the queue to stop
     * mutating before calling fetchUnitSlots with all the unitSlotIds.
     */
    initUnitSlot(unitSlotId: number) {
      this.queueUnitSlot(unitSlotId)
      const newIndex = this.queuedSlots.length - 1

      // have to save a copy of "this" to pass into debounced arrow function
      const unitsStore = this
      this.fetchUnitSlots(unitsStore)

      return newIndex
    },
  },
})
